/**
 * Assistant Task (todo) helpers — Control Centre Tasks product.
 * Creates/finds/updates in the BACKGROUND via todos.ts (no UI form).
 * Tool IDs use todo_* so they never collide with calendar Event tools (*_task).
 */
import { supabase } from '../supabase';
import { getResources } from '../resources';
import { findEventsForTodo, findEventsForTodos, type CalendarEventRow } from '../calendarEvents';
import {
  bulkDeleteTodoItems,
  completeTodoItem,
  countTodoItemsForBulkDelete,
  createTodoItem,
  createTodoList,
  deleteTodoItem,
  findTodoItems,
  getTodoLists,
  isTodoOverdue,
  reopenTodoItem,
  updateTodoItem,
  type TodoItemPriority,
  type TodoItemWithRelations,
  type TodoList,
  type TodoViewer,
} from '../todos';

/**
 * Who the assistant is acting as — mirrors PermissionContext: the signed-in
 * user's resource row for this business, or the admin/owner world when they
 * have no resource row. Keeps the AI inside the same visible task world as
 * the Tasks UI. Cached briefly (per business+user) — tool calls come in bursts.
 */
let todoViewerCache: { key: string; at: number; viewer: TodoViewer } | null = null;

export async function resolveTodoViewer(businessId: string): Promise<TodoViewer> {
  const fallback: TodoViewer = { resourceId: null, isAdmin: true };
  if (!supabase) return fallback;
  try {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) return fallback;

    const key = `${businessId}:${userId}`;
    if (todoViewerCache && todoViewerCache.key === key && Date.now() - todoViewerCache.at < 60_000) {
      return todoViewerCache.viewer;
    }

    const { data } = await supabase
      .from('resources')
      .select('id, is_admin')
      .eq('business_id', businessId)
      .eq('auth_user_id', userId)
      .maybeSingle();

    // No resource row = business owner (same rule PermissionContext uses).
    const viewer: TodoViewer = data
      ? { resourceId: data.id as string, isAdmin: !!data.is_admin }
      : fallback;
    todoViewerCache = { key, at: Date.now(), viewer };
    return viewer;
  } catch {
    return fallback;
  }
}

function notifyChanged(businessId: string, todoId?: string): void {
  window.dispatchEvent(
    new CustomEvent('todoItemsChanged', { detail: { businessId, todoId } })
  );
}

function personName(row: {
  first_name?: string | null;
  last_name?: string | null;
} | null | undefined): string {
  if (!row) return '';
  return `${row.first_name || ''} ${row.last_name || ''}`.trim();
}

function describeTodo(item: TodoItemWithRelations, linkedEvent?: CalendarEventRow | null): string {
  const bits: string[] = [`"${item.title}"`];
  if (item.list?.name) bits.push(`list: ${item.list.name}`);
  if (item.due_date) {
    bits.push(
      item.due_time
        ? `due ${item.due_date} ${String(item.due_time).slice(0, 5)}`
        : `due ${item.due_date}`
    );
  }
  const who = personName(item.assignee);
  if (who) bits.push(`assigned: ${who}`);
  const cust = personName(item.customer);
  if (cust) bits.push(`customer: ${cust}`);
  if (item.status === 'completed') bits.push('completed');
  if (item.priority && item.priority !== 'none') bits.push(`priority: ${item.priority}`);
  if (item.estimated_minutes) bits.push(`estimate: ${item.estimated_minutes} min`);
  bits.push(`todo_id: ${item.id}`);
  if (linkedEvent) {
    bits.push(
      `linked calendar Event: "${linkedEvent.title}" ${linkedEvent.start_date} ${String(linkedEvent.start_time || '').slice(0, 5)} (task_id: ${linkedEvent.id})`
    );
  }
  return bits.join(' — ');
}

function normalizeKey(s: string): string {
  return s
    .toLowerCase()
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function editDistance(a: string, b: string): number {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const dp = Array.from({ length: a.length + 1 }, (_, i) =>
    Array.from({ length: b.length + 1 }, (_, j) => (i === 0 ? j : j === 0 ? i : 0))
  );
  for (let i = 1; i <= a.length; i++) {
    for (let j = 1; j <= b.length; j++) {
      dp[i][j] = Math.min(
        dp[i - 1][j] + 1,
        dp[i][j - 1] + 1,
        dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
      );
    }
  }
  return dp[a.length][b.length];
}

/** How closely two tokens match (0 = no match). Handles typos like brite↔bright. */
function tokenCloseScore(a: string, b: string): number {
  if (!a || !b) return 0;
  if (a === b) return 10;
  if (a.length >= 3 && (b.startsWith(a) || a.startsWith(b))) return 8;
  if (a.includes(b) || b.includes(a)) {
    const shorter = Math.min(a.length, b.length);
    if (shorter >= 3) return 7;
  }
  const d = editDistance(a, b);
  const minLen = Math.min(a.length, b.length);
  const maxLen = Math.max(a.length, b.length);
  if (d === 1 && minLen >= 3) return 8;
  if (d === 2 && minLen >= 4) return 7;
  // "brite" ↔ "bright" is distance 3 — still a clear human typo when words share a prefix
  let prefix = 0;
  while (prefix < minLen && a[prefix] === b[prefix]) prefix += 1;
  if (d <= 3 && prefix >= 3 && d / maxLen <= 0.55) return 6;
  if (d === 3 && minLen >= 5 && d / maxLen <= 0.5) return 5;
  return 0;
}

/** Score how well a spoken/typed query matches a list name. Higher = better. */
export function scoreTodoListName(query: string, listName: string): number {
  const q = normalizeKey(query);
  const n = normalizeKey(listName);
  if (!q || !n) return 0;
  if (q === n) return 1000;
  if (n.includes(q) || q.includes(n)) return 800 + Math.min(q.length, n.length);

  const qTokens = q.split(' ').filter(Boolean);
  const nTokens = n.split(' ').filter(Boolean);
  if (qTokens.length === 0 || nTokens.length === 0) return 0;

  let score = 0;
  let matched = 0;
  for (const qt of qTokens) {
    let best = 0;
    for (const nt of nTokens) {
      best = Math.max(best, tokenCloseScore(qt, nt));
    }
    if (best > 0) matched += 1;
    score += best;
  }

  // Require every query token to have a decent hit when the query is short
  const needAll = qTokens.length <= 3;
  if (needAll && matched < qTokens.length) return 0;
  if (!needAll && matched < Math.ceil(qTokens.length * 0.75)) return 0;

  if (qTokens.length === nTokens.length) score += 2;
  return score;
}

/** Map spoken intent / list name → best matching list, or null for Inbox default. */
export function resolveTodoListId(
  lists: TodoList[],
  opts: { listName?: string; title?: string; intentHint?: string } = {}
): { listId: string | null; matchedName: string | null; reason: string } {
  const inbox = lists.find((l) => l.is_default) || lists[0] || null;
  const explicit = String(opts.listName || '').trim();
  if (explicit) {
    const key = normalizeKey(explicit);
    const exact = lists.find((l) => normalizeKey(l.name) === key);
    if (exact) return { listId: exact.id, matchedName: exact.name, reason: 'explicit' };

    const scored = lists
      .map((l) => ({ list: l, score: scoreTodoListName(explicit, l.name) }))
      .filter((x) => x.score > 0)
      .sort((a, b) => b.score - a.score);

    if (scored.length > 0) {
      const top = scored[0];
      const second = scored[1];
      // Clear winner, or only one hit — take it (typos like "brite"→"bright")
      const clear =
        !second ||
        top.score >= second.score + 3 ||
        (top.score >= 14 && top.score > second.score);
      if (clear && top.score >= 8) {
        return {
          listId: top.list.id,
          matchedName: top.list.name,
          reason: top.score >= 800 ? 'explicit-fuzzy' : 'explicit-typo',
        };
      }
      // Near-tie among strong matches → ask
      const contenders = scored.filter((x) => x.score >= Math.max(8, top.score - 2));
      if (contenders.length > 1) {
        return {
          listId: null,
          matchedName: null,
          reason: `ambiguous-list:${contenders.map((c) => c.list.name).join('|')}`,
        };
      }
      if (top.score >= 8) {
        return {
          listId: top.list.id,
          matchedName: top.list.name,
          reason: 'explicit-typo',
        };
      }
    }

    return { listId: null, matchedName: null, reason: `missing-list:${explicit}` };
  }

  // Auto-route from title / intent words onto similarly named custom lists
  const hay = normalizeKey(`${opts.intentHint || ''} ${opts.title || ''}`);
  const custom = lists.filter((l) => !l.is_default);
  const scored = custom
    .map((l) => {
      const name = normalizeKey(l.name);
      if (!name || name.length < 3) return { list: l, score: 0 };
      const stem = name.replace(/s$/, '');
      let score = 0;
      if (hay.includes(name) || hay.includes(stem)) score = name.length;
      if (/remind/.test(name) && /remind/.test(hay)) score = Math.max(score, 8);
      if (/follow\s*up|followup/.test(name) && /follow\s*up|followup/.test(hay)) {
        score = Math.max(score, 8);
      }
      if (/^(to\s*do|todos?)$/.test(name) && /\b(to\s*do|todos?)\b/.test(hay)) {
        score = Math.max(score, 6);
      }
      // Also score full-phrase typo match against the haystack phrase
      score = Math.max(score, scoreTodoListName(hay, l.name) >= 14 ? 12 : 0);
      return { list: l, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);

  if (scored.length > 0 && scored[0].score >= 6) {
    if (scored.length === 1 || scored[0].score > scored[1].score) {
      return {
        listId: scored[0].list.id,
        matchedName: scored[0].list.name,
        reason: 'auto-intent',
      };
    }
  }

  return {
    listId: inbox?.id || null,
    matchedName: inbox?.name || 'Inbox',
    reason: 'inbox-default',
  };
}

async function resolveAssignee(
  businessId: string,
  query: string
): Promise<{ id: string; name: string } | { error: string }> {
  const q = query.trim();
  if (!q) return { error: 'assignee name is empty' };
  const team = (await getResources(businessId)).filter((r) => r.status === 'active');
  const tokens = q.toLowerCase().split(/\s+/).filter((t) => t.length > 0);
  const nameOf = (r: { first_name?: string; last_name?: string }) =>
    `${r.first_name || ''} ${r.last_name || ''}`.trim();

  const editDistance = (a: string, b: string): number => {
    const dp = Array.from({ length: a.length + 1 }, (_, i) => [i, ...Array(b.length).fill(0)]);
    for (let j = 0; j <= b.length; j++) dp[0][j] = j;
    for (let i = 1; i <= a.length; i++) {
      for (let j = 1; j <= b.length; j++) {
        dp[i][j] = Math.min(
          dp[i - 1][j] + 1,
          dp[i][j - 1] + 1,
          dp[i - 1][j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1)
        );
      }
    }
    return dp[a.length][b.length];
  };
  const tokenClose = (t: string, nameToken: string): boolean => {
    if (nameToken.includes(t) || t.includes(nameToken)) return true;
    if (t.length < 4) return false;
    return editDistance(t, nameToken) <= (t.length >= 6 ? 2 : 1);
  };
  const scoreMatch = (r: (typeof team)[0], fuzzy: boolean): boolean => {
    const nameTokens = nameOf(r).toLowerCase().split(/\s+/).filter(Boolean);
    return tokens.every((t) =>
      nameTokens.some((nt) => (fuzzy ? tokenClose(t, nt) : nt.includes(t) || t.includes(nt)))
    );
  };

  let matched = team.filter((r) => scoreMatch(r, false));
  if (matched.length === 0) matched = team.filter((r) => scoreMatch(r, true));
  if (matched.length === 0) {
    return {
      error: `no team member matches "${q}". Team: ${team.map(nameOf).filter(Boolean).join(', ') || 'none'}.`,
    };
  }
  if (matched.length > 1) {
    return {
      error: `several team members match "${q}": ${matched.map(nameOf).join(', ')}. Ask which one.`,
    };
  }
  return { id: matched[0].id, name: nameOf(matched[0]) };
}

/**
 * Whose task world a tool call operates in. No person → the caller's own
 * world. With a person (admin only) → that resource's world, exactly like
 * the "Viewing" dropdown in the Tasks modal.
 */
async function resolveViewerForPerson(
  businessId: string,
  person?: string
): Promise<{ viewer: TodoViewer; label: string | null } | { error: string }> {
  const own = await resolveTodoViewer(businessId);
  const q = String(person || '').trim();
  if (!q || /^(me|my|myself|mine)$/i.test(q)) return { viewer: own, label: null };
  if (!own.isAdmin) {
    return { error: 'only admins can view or manage another person\'s Tasks — you can only work with your own.' };
  }
  const hit = await resolveAssignee(businessId, q);
  if ('error' in hit) return { error: hit.error };
  if (own.resourceId && hit.id === own.resourceId) return { viewer: own, label: null };
  return { viewer: { resourceId: hit.id, isAdmin: false }, label: hit.name };
}

export async function listTodoListsForAssistant(
  businessId: string,
  opts: { person?: string } = {}
): Promise<string> {
  const scoped = await resolveViewerForPerson(businessId, opts.person);
  if ('error' in scoped) return `error: ${scoped.error}`;
  const { viewer, label } = scoped;
  const lists = await getTodoLists(businessId, { viewer });
  const whose = label ? `${label}'s` : 'your';
  if (lists.length === 0) {
    return `No Task lists for ${whose} view yet — an Inbox is created on the first Task.`;
  }
  return `${label ? `${label}'s Task lists` : 'Task lists'} (Control Centre Tasks — not calendar Events):\n${lists
    .map((l) => `- "${l.name}"${l.is_default ? ' (Inbox / default)' : ''} — list_id: ${l.id}`)
    .join('\n')}`;
}

export async function createTodoListForAssistant(
  businessId: string,
  opts: { name: string; person?: string }
): Promise<string> {
  const name = String(opts.name || '').trim();
  if (!name) return 'error: create_todo_list needs a name (e.g. "Reminders", "Follow-ups").';
  if (/^inbox$/i.test(name)) {
    return 'error: Inbox already exists as the default list — pick a different name.';
  }

  const scoped = await resolveViewerForPerson(businessId, opts.person);
  if ('error' in scoped) return `error: ${scoped.error}`;
  const { viewer, label } = scoped;
  const before = await getTodoLists(businessId, { viewer });
  const existing = before.find((l) => normalizeKey(l.name) === normalizeKey(name));
  if (existing) {
    return `List "${existing.name}" already exists${label ? ` in ${label}'s Tasks` : ''} — list_id: ${existing.id}. Use it with create_todo.`;
  }

  const list = await createTodoList(businessId, name, {
    created_by_resource_id: viewer.resourceId,
  });
  notifyChanged(businessId);
  return `Created Task list "${list.name}"${label ? ` for ${label} — it lives in their Tasks view` : ''} — list_id: ${list.id}. Ready for create_todo.`;
}

export async function createTodoForAssistant(
  businessId: string,
  opts: {
    title: string;
    notes?: string;
    list?: string;
    intent_hint?: string;
    due_date?: string;
    due_time?: string;
    customer_id?: string;
    assignee?: string;
    priority?: string;
    /** Required time to complete — minutes (e.g. 180 for a 3-hour task). */
    estimated_minutes?: number | null;
    /** Admin only: create in this team member's task world (their lists/Inbox). */
    person?: string;
  }
): Promise<string> {
  const title = String(opts.title || '').trim();
  if (!title) return 'error: create_todo needs a title — phrase one from the user\'s words and create immediately.';

  const scoped = await resolveViewerForPerson(businessId, opts.person);
  if ('error' in scoped) return `error: ${scoped.error}`;
  const { viewer, label: personLabel } = scoped;
  const lists = await getTodoLists(businessId, { viewer });
  let listName = opts.list;
  // Explicit list name that doesn't exist yet → create it (user named the list)
  if (listName && String(listName).trim()) {
    const resolvedProbe = resolveTodoListId(lists, { listName: listName });
    if (resolvedProbe.reason.startsWith('missing-list:')) {
      const created = await createTodoList(businessId, String(listName).trim(), {
        created_by_resource_id: viewer.resourceId,
      });
      lists.push(created);
      listName = created.name;
      notifyChanged(businessId);
    }
  }

  const resolved = resolveTodoListId(lists, {
    listName,
    title,
    intentHint: opts.intent_hint,
  });
  if (resolved.reason.startsWith('ambiguous-list:')) {
    const names = resolved.reason.slice('ambiguous-list:'.length).split('|');
    return `error: several lists match — ask which one: ${names.join(', ')}.`;
  }
  if (resolved.reason.startsWith('missing-list:')) {
    const wanted = resolved.reason.slice('missing-list:'.length);
    return `error: no list named "${wanted}". Available: ${lists.map((l) => l.name).join(', ')}. Create it with create_todo_list first, or omit list to use Inbox.`;
  }

  let assigneeId: string | null = null;
  let assigneeLabel = '';
  const assigneeQ = String(opts.assignee || '').trim();
  if (assigneeQ) {
    const hit = await resolveAssignee(businessId, assigneeQ);
    if ('error' in hit) return `error: ${hit.error}`;
    assigneeId = hit.id;
    assigneeLabel = hit.name;
  }

  const priorityRaw = String(opts.priority || '').trim().toLowerCase();
  const priority: TodoItemPriority =
    priorityRaw === 'low' || priorityRaw === 'medium' || priorityRaw === 'high'
      ? priorityRaw
      : 'none';

  const dueDate = String(opts.due_date || '').trim() || null;
  const dueTime = dueDate ? String(opts.due_time || '').trim() || null : null;
  if (dueDate && !/^\d{4}-\d{2}-\d{2}$/.test(dueDate)) {
    return 'error: due_date must be YYYY-MM-DD (resolve relative dates from the CURRENT DATE line first).';
  }

  let estimatedMinutes: number | null | undefined;
  if (opts.estimated_minutes !== undefined && opts.estimated_minutes !== null) {
    const mins = Number(opts.estimated_minutes);
    if (!Number.isFinite(mins) || mins < 1 || mins > 1440) {
      return 'error: estimated_minutes must be between 1 and 1440 (e.g. 180 for 3 hours).';
    }
    estimatedMinutes = Math.round(mins);
  }

  // Resources' AI defaults new tasks to themselves; same when an admin is
  // creating in someone else's world ("add a task for John"). Admins working
  // in their own world stay unassigned unless they name someone.
  if (!assigneeId && !viewer.isAdmin && viewer.resourceId) {
    assigneeId = viewer.resourceId;
    if (personLabel) assigneeLabel = personLabel;
  }

  const item = await createTodoItem(
    businessId,
    {
      title,
      description: String(opts.notes || '').trim() || null,
      list_id: resolved.listId,
      due_date: dueDate,
      due_time: dueTime,
      customer_id: String(opts.customer_id || '').trim() || null,
      assignee_resource_id: assigneeId,
      priority,
      estimated_minutes: estimatedMinutes,
      created_by_resource_id: viewer.resourceId,
    },
    viewer
  );

  notifyChanged(businessId, item.id);

  const bits = [`Created Task "${item.title}"`];
  if (item.list?.name) {
    bits.push(personLabel ? `in ${personLabel}'s "${item.list.name}" list` : `in ${item.list.name}`);
  }
  if (item.due_date) {
    bits.push(
      item.due_time
        ? `due ${item.due_date} ${String(item.due_time).slice(0, 5)}`
        : `due ${item.due_date}`
    );
  }
  if (item.estimated_minutes) {
    const m = item.estimated_minutes;
    bits.push(
      m >= 60 && m % 60 === 0
        ? `${m / 60}h required`
        : m >= 60
          ? `${Math.floor(m / 60)}h ${m % 60}m required`
          : `${m}m required`
    );
  }
  if (assigneeLabel) bits.push(`assigned to ${assigneeLabel}`);
  if (item.customer) bits.push(`for ${personName(item.customer)}`);
  if (resolved.reason === 'auto-intent' && item.list?.name) {
    bits.push(`(auto-filed to ${item.list.name} from your wording)`);
  }
  bits.push(`todo_id: ${item.id}`);
  return `${bits.join(' — ')}. Saved in the background — nothing else to click.`;
}

export async function findTodosForAssistant(
  businessId: string,
  opts: {
    query?: string;
    list?: string;
    status?: string;
    due_date?: string;
    overdue?: boolean;
    important?: boolean;
    assignee?: string;
    customer_id?: string;
    /** Admin only: search this team member's task world instead of your own. */
    person?: string;
    limit?: number;
  } = {}
): Promise<string> {
  const scoped = await resolveViewerForPerson(businessId, opts.person);
  if ('error' in scoped) return `error: ${scoped.error}`;
  const { viewer, label: personLabel } = scoped;
  const whose = personLabel ? `${personLabel}'s` : '';
  const lists = await getTodoLists(businessId, { viewer });
  let listId: string | undefined;
  const listQ = String(opts.list || '').trim();
  if (listQ) {
    const resolved = resolveTodoListId(lists, { listName: listQ });
    if (resolved.reason.startsWith('ambiguous-list:')) {
      return `error: several lists match — ask which: ${resolved.reason.slice('ambiguous-list:'.length).split('|').join(', ')}.`;
    }
    if (resolved.reason.startsWith('missing-list:') || !resolved.listId) {
      return `error: no list named "${listQ}". Lists: ${lists.map((l) => l.name).join(', ')}.`;
    }
    listId = resolved.listId;
  }

  let assigneeId: string | undefined;
  const assigneeQ = String(opts.assignee || '').trim();
  if (assigneeQ) {
    const hit = await resolveAssignee(businessId, assigneeQ);
    if ('error' in hit) return `error: ${hit.error}`;
    assigneeId = hit.id;
  }

  const statusRaw = String(opts.status || 'open').trim().toLowerCase();
  const status =
    statusRaw === 'all' || statusRaw === 'completed' || statusRaw === 'open'
      ? (statusRaw as 'open' | 'completed' | 'all')
      : 'open';

  const today = new Date();
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

  // "overdue" matches the Tasks panel's Today bucket: open items due today or
  // earlier. The response then separates truly overdue (due moment passed)
  // from items due later today, so the assistant never answers "no overdue
  // tasks" while the panel shows a Today count.
  let items = await findTodoItems(businessId, {
    query: String(opts.query || '').trim() || undefined,
    list_id: listId,
    status: opts.important ? 'open' : status,
    due_date: String(opts.due_date || '').trim() || undefined,
    actually_due: opts.overdue ? true : undefined,
    assignee_resource_id: assigneeId,
    customer_id: String(opts.customer_id || '').trim() || undefined,
    visible_to: viewer,
    limit: Math.min(Math.max(opts.limit ?? 40, 1), 100),
  });

  // Linked calendar Events (earliest per Task) — surfaced so the assistant
  // KNOWS a Task is scheduled and can offer to move the Event when the Task's
  // due date / required time changes.
  let eventByTodo = new Map<string, CalendarEventRow>();
  try {
    eventByTodo = await findEventsForTodos(businessId, items.map((t) => t.id));
  } catch { /* non-critical — describe without event info */ }
  const describe = (t: TodoItemWithRelations) => describeTodo(t, eventByTodo.get(t.id));
  const eventFootnote = eventByTodo.size > 0
    ? '\nTasks with a linked calendar Event: if the user changes that Task\'s due date/time or required time, ask whether to reschedule the Event too (get_free_slots → update_task).'
    : '';

  if (opts.important) {
    // Rank: overdue > due today/soon > high priority > has customer > rest
    const todayMidnight = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    ).getTime();
    const score = (t: TodoItemWithRelations): number => {
      let s = 0;
      if (t.due_date && t.due_date < todayStr) s += 100;
      else if (t.due_date === todayStr) s += 70;
      else if (t.due_date) {
        const due = new Date(t.due_date + 'T00:00:00').getTime();
        const days = Math.round((due - todayMidnight) / 86400000);
        if (days >= 0 && days <= 7) s += 40;
      }
      if (t.priority === 'high') s += 30;
      else if (t.priority === 'medium') s += 15;
      if (t.customer_id) s += 10;
      if (t.assignee_resource_id) s += 5;
      return s;
    };
    items = [...items].sort((a, b) => score(b) - score(a)).filter((t) => score(t) > 0);
    if (items.length === 0) {
      return `No ${whose ? `of ${whose} ` : ''}Tasks look especially important right now (nothing overdue, due soon, high priority, or customer-linked).`;
    }
    return `Important open Tasks${personLabel ? ` for ${personLabel}` : ''} (ranked):\n${items
      .slice(0, 12)
      .map((t, i) => `${i + 1}. ${describe(t)}`)
      .join('\n')}${eventFootnote}`;
  }

  if (opts.overdue && items.length > 0) {
    const late = items.filter((t) => isTodoOverdue(t));
    const dueLaterToday = items.filter((t) => !isTodoOverdue(t));
    const lines = [
      ...late.map((t) => `- OVERDUE — ${describe(t)}`),
      ...dueLaterToday.map((t) => `- Due today, time not passed yet — ${describe(t)}`),
    ];
    return `${items.length} open Task(s) on ${whose ? `${whose} ` : ''}today's plate (same as the Tasks "Today" bucket): ${late.length} overdue, ${dueLaterToday.length} due later today. Report BOTH groups to the user — never say "nothing due" when this list is non-empty:\n${lines.join('\n')}${eventFootnote}`;
  }

  if (items.length === 0) {
    return `No ${whose ? `${whose} ` : ''}Tasks found${opts.query ? ` matching "${opts.query}"` : ''}${opts.overdue ? ' that are due today or overdue' : ''}${opts.due_date ? ` due ${opts.due_date}` : ''} — tell the user plainly.`;
  }
  if (items.length === 1) {
    return `Found exactly one ${whose ? `of ${whose} ` : ''}Task${whose ? 's' : ''}: ${describe(items[0])}. Act with update_todo if needed — no need to ask.${eventFootnote}`;
  }
  return `${items.length} ${whose ? `${whose} ` : ''}Tasks match — if the user asked you to change one, ask which (cite due dates / lists), then update_todo with that todo_id:\n${items
    .slice(0, 20)
    .map((t) => `- ${describe(t)}`)
    .join('\n')}${eventFootnote}`;
}

export async function updateTodoForAssistant(
  businessId: string,
  opts: {
    todo_id: string;
    status?: string;
    title?: string;
    notes?: string;
    list?: string;
    due_date?: string | null;
    due_time?: string | null;
    assignee?: string | null;
    customer_id?: string | null;
    priority?: string;
    estimated_minutes?: number | null;
    delete?: boolean;
    /** Admin only: resolve list moves against this team member's lists. */
    person?: string;
  }
): Promise<string> {
  const todoId = String(opts.todo_id || '').trim();
  if (!todoId) return 'error: update_todo needs todo_id from find_todos.';

  if (opts.delete === true) {
    await deleteTodoItem(businessId, todoId);
    notifyChanged(businessId, todoId);
    return `Deleted that Task. The list has refreshed.`;
  }

  if (opts.status === 'completed') {
    const item = await completeTodoItem(businessId, todoId);
    notifyChanged(businessId, todoId);
    return `Completed Task "${item.title}".`;
  }
  if (opts.status === 'open') {
    const item = await reopenTodoItem(businessId, todoId);
    notifyChanged(businessId, todoId);
    return `Reopened Task "${item.title}".`;
  }

  const patch: Parameters<typeof updateTodoItem>[2] = {};
  if (opts.title !== undefined && String(opts.title).trim()) patch.title = String(opts.title).trim();
  if (opts.notes !== undefined) patch.description = String(opts.notes).trim() || null;
  if (opts.due_date !== undefined) {
    patch.due_date = opts.due_date === null || opts.due_date === '' ? null : String(opts.due_date);
  }
  if (opts.due_time !== undefined) {
    patch.due_time = opts.due_time === null || opts.due_time === '' ? null : String(opts.due_time);
  }
  if (opts.customer_id !== undefined) {
    patch.customer_id = opts.customer_id === null || opts.customer_id === '' ? null : String(opts.customer_id);
  }
  if (opts.priority !== undefined) {
    const p = String(opts.priority).trim().toLowerCase();
    if (p === 'none' || p === 'low' || p === 'medium' || p === 'high') patch.priority = p;
  }
  if (opts.estimated_minutes !== undefined) {
    if (opts.estimated_minutes === null) patch.estimated_minutes = null;
    else patch.estimated_minutes = Number(opts.estimated_minutes);
  }

  if (opts.list !== undefined && String(opts.list).trim()) {
    const scoped = await resolveViewerForPerson(businessId, opts.person);
    if ('error' in scoped) return `error: ${scoped.error}`;
    const lists = await getTodoLists(businessId, { viewer: scoped.viewer });
    const resolved = resolveTodoListId(lists, { listName: String(opts.list) });
    if (!resolved.listId || resolved.reason.startsWith('ambiguous') || resolved.reason.startsWith('missing')) {
      return `error: could not move to list "${opts.list}". Lists: ${lists.map((l) => l.name).join(', ')}.`;
    }
    patch.list_id = resolved.listId;
  }

  if (opts.assignee !== undefined) {
    if (opts.assignee === null || String(opts.assignee).trim() === '') {
      patch.assignee_resource_id = null;
    } else {
      const hit = await resolveAssignee(businessId, String(opts.assignee));
      if ('error' in hit) return `error: ${hit.error}`;
      patch.assignee_resource_id = hit.id;
    }
  }

  if (Object.keys(patch).length === 0) {
    return 'error: nothing to change — pass status, title, notes, due_date, list, assignee, customer_id, priority, estimated_minutes, or delete.';
  }

  const item = await updateTodoItem(businessId, todoId, patch);
  notifyChanged(businessId, todoId);

  // Scheduling-relevant change on a Task with a linked calendar Event: the
  // Event does NOT move on its own — surface it so the assistant asks the
  // user whether to reschedule/resize it instead of leaving it stale.
  const schedulingChanged =
    patch.due_date !== undefined ||
    patch.due_time !== undefined ||
    patch.estimated_minutes !== undefined;
  if (schedulingChanged) {
    try {
      const events = (await findEventsForTodo(businessId, todoId)).filter(
        (e) => e.status !== 'completed'
      );
      if (events.length > 0) {
        const list = events
          .map((e) => `"${e.title}" on ${e.start_date} ${String(e.start_time || '').slice(0, 5)}–${String(e.end_time || '').slice(0, 5)} (task_id: ${e.id})`)
          .join('; ');
        return `Updated Task: ${describeTodo(item)}. Saved. IMPORTANT — this Task has a linked calendar Event that was NOT moved: ${list}. Ask the user if the Event should be rescheduled${patch.estimated_minutes !== undefined ? ' or resized to the new required time' : ' to match the new due date'}; if yes, use get_free_slots (duration = the Task's estimated_minutes) and update_task with that task_id. Do not silently leave the Event at the old time.`;
      }
    } catch { /* non-critical — fall through to plain confirmation */ }
  }

  return `Updated Task: ${describeTodo(item)}. Saved.`;
}

/**
 * Bulk delete Tasks — "delete all tasks in the Follow-ups list", "clear my
 * completed tasks". Two-step contract so a fuzzy list match never wipes the
 * wrong list: called WITHOUT confirm it only previews (matched list + count);
 * called WITH confirm=true it deletes.
 */
export async function bulkDeleteTodosForAssistant(
  businessId: string,
  opts: { list?: string; status?: string; confirm?: boolean; person?: string } = {}
): Promise<string> {
  const statusRaw = String(opts.status || '').trim().toLowerCase();
  const status: 'open' | 'completed' | 'all' =
    statusRaw === 'open' || statusRaw === 'completed' ? statusRaw : 'all';

  const scoped = await resolveViewerForPerson(businessId, opts.person);
  if ('error' in scoped) return `error: ${scoped.error}`;
  const { viewer, label: personLabel } = scoped;
  const listQ = String(opts.list || '').trim();
  let listId: string | undefined;
  let listName: string | undefined;
  if (listQ) {
    const lists = await getTodoLists(businessId, { viewer });
    const resolved = resolveTodoListId(lists, { listName: listQ });
    if (resolved.reason.startsWith('ambiguous-list:')) {
      return `error: several lists match "${listQ}" — ask which: ${resolved.reason.slice('ambiguous-list:'.length).split('|').join(', ')}. Nothing was deleted.`;
    }
    if (resolved.reason.startsWith('missing-list:') || !resolved.listId) {
      return `error: no list named "${listQ}". Lists: ${lists.map((l) => l.name).join(', ')}. Nothing was deleted.`;
    }
    listId = resolved.listId;
    listName = resolved.matchedName || listQ;
  }

  if (!listId && status !== 'completed') {
    return 'error: bulk delete needs a list, or status "completed" to clear completed tasks everywhere. Deleting every open task across all lists in one call is not allowed — go list by list. Nothing was deleted.';
  }

  const scopeLabel = `${status === 'all' ? 'all' : status} task(s)${listName ? ` in list "${listName}"` : ' across all lists'}${personLabel ? ` in ${personLabel}'s Tasks` : ''}`;

  if (opts.confirm !== true) {
    const count = await countTodoItemsForBulkDelete(businessId, {
      listId,
      status,
      visibleTo: viewer,
    });
    if (count === 0) {
      return `No ${scopeLabel} to delete — tell the user the list is already empty for that scope.`;
    }
    return `PREVIEW ONLY — nothing deleted yet. This would delete ${count} ${scopeLabel}. Read that back to the user and get an explicit yes, then call bulk_delete_todos again with the same scope plus confirm=true.`;
  }

  const deleted = await bulkDeleteTodoItems(businessId, { listId, status, visibleTo: viewer });
  notifyChanged(businessId);
  return `Deleted ${deleted} ${scopeLabel}. The Tasks panel has refreshed.`;
}
